import { Component, EventEmitter, Input, Output } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { DsButtonVariants, Icons, IconStyles } from '@levelaccess/design-system';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { VariationValue } from '@harnessio/ff-javascript-client-sdk';
import { FeatureFlagService } from '../../../../../../services/feature-flag/feature-flag.service';
import { FeatureFlagCollection } from '../../../../../../../../shared/interfaces/feature-flag.interface';
import { TranslateService } from '../../../../../../translate/translate.service';

export enum BannerStatus {
  default = 'default',
  findingRestore = 'findingRestore',
  selectAtLeastOne = 'selectAtLeastOne',
  selectLinkedFinding = 'selectLinkedFinding',
  selectLinkedClosedFinding = 'selectLinkedClosedFinding',
  selectClosedFinding = 'selectClosedFinding',
  selectDismissedFinding = 'selectDismissedFinding',
  taskCreated = 'taskCreated',
  findingsDismissed = 'findingsDismissed',
}

const BannerStatusMessage: Readonly<Record<BannerStatus, string>> = {
  [BannerStatus.default]: 'banner_select_unlinked_finding',
  [BannerStatus.findingRestore]: 'banner_finding_restore',
  [BannerStatus.selectAtLeastOne]: 'banner_select_at_least_one_checkbox',
  [BannerStatus.selectLinkedFinding]: 'banner_select_only_unlinked_finding',
  [BannerStatus.selectLinkedClosedFinding]: 'banner_select_linked_closed_finding',
  [BannerStatus.selectClosedFinding]: 'banner_select_closed_finding',
  [BannerStatus.selectDismissedFinding]: 'banner_select_only_not_dismissed_finding',
  [BannerStatus.taskCreated]: 'banner_task_created',
  [BannerStatus.findingsDismissed]: 'banner_finding_dismissed',
};

const BannerStatusMessageNewFF: Readonly<Record<BannerStatus, string>> = {
  ...BannerStatusMessage,
  [BannerStatus.default]: 'banner_select_dismiss_restore',
  [BannerStatus.selectAtLeastOne]: 'banner_select_at_least_one_checkbox_new_ff',
  [BannerStatus.selectLinkedFinding]: 'banner_select_only_unlinked_finding_new_ff',
  [BannerStatus.selectDismissedFinding]: 'banner_select_only_not_dismissed_finding_new_ff',
};

enum BannerStyle {
  success,
  info,
  error,
}

const BannerStatusToStyle: Readonly<Record<BannerStatus, BannerStyle>> = {
  [BannerStatus.default]: BannerStyle.info,
  [BannerStatus.findingRestore]: BannerStyle.success,
  [BannerStatus.selectAtLeastOne]: BannerStyle.error,
  [BannerStatus.selectLinkedFinding]: BannerStyle.error,
  [BannerStatus.selectLinkedClosedFinding]: BannerStyle.error,
  [BannerStatus.selectClosedFinding]: BannerStyle.error,
  [BannerStatus.selectDismissedFinding]: BannerStyle.error,
  [BannerStatus.taskCreated]: BannerStyle.success,
  [BannerStatus.findingsDismissed]: BannerStyle.success,
};

@Component({
  selector: 'app-issues-view-banner',
  templateUrl: './issues-view-banner.component.html',
  styleUrls: ['./issues-view-banner.component.scss'],
  standalone: false,
})
export class IssuesViewBannerComponent {
  @Input()
  public set bannerStatus(bannerStatus: BannerStatus) {
    this.showBanner = true;
    this._bannerStatus = bannerStatus;
  }

  public get bannerStatus(): BannerStatus {
    return this._bannerStatus;
  }

  @Input() public taskLink: string;
  @Input() public taskProjectId: string;

  @Input() public showBanner: boolean;
  @Output() public showBannerChange: EventEmitter<boolean>;
  public _bannerStatus: BannerStatus;

  public BannerStatusMessage: Readonly<Record<BannerStatus, string>>;
  public BannerStatusToStyle: Readonly<Record<BannerStatus, BannerStyle>>;
  public BannerStyle: typeof BannerStyle;
  public readonly Icons: typeof Icons = Icons;
  public IconStyles: typeof IconStyles = IconStyles;
  public readonly DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;

  public isNewScanBannerEnabled$: Observable<boolean>;

  constructor(
    private featureFlagService: FeatureFlagService,
    private translateService: TranslateService,
  ) {
    this.BannerStatusMessage = BannerStatusMessage;
    this.BannerStatusToStyle = BannerStatusToStyle;
    this.BannerStyle = BannerStyle;
    this.showBanner = true;
    this.showBannerChange = new EventEmitter<boolean>();

    this.isNewScanBannerEnabled$ = this.featureFlagService
      .variation$(FeatureFlagCollection.scanBanner, false)
      .pipe(map((value: VariationValue): boolean => Boolean(value)));
  }

  public get bannerTextMultiLineNewFF(): string[] {
    return this.translateService.instant(BannerStatusMessageNewFF[this.bannerStatus]).split('\n');
  }

  public get bannerHtmlNewFF(): SafeHtml {
    const statusWords = this.bannerStatus === BannerStatus.taskCreated ? [this.taskLink, this.taskProjectId] : [];
    return this.translateService.instantHtml(BannerStatusMessageNewFF[this.bannerStatus], statusWords);
  }

  public closeBanner(): void {
    this.showBanner = false;
    this.showBannerChange.emit(false);
  }
}
